/**
 * Shared drawer atmosphere components — Parity Sprint.
 *
 * Extracted from Maison's MaisonDetailDrawer so /table, /stay, /fit,
 * /enjoy, /escape, /airport, /mobility and /club can all wear the same
 * "Very Luxy, Very Classy" feel: brass-stamped voice lines, "around
 * the corner" cross-pillar whispers, member-signal whispers, "trusted
 * voices" reviews, and a pinned brief footer.
 *
 * Founder ruling 30 Jun 2026: every pillar's drawer should feel like
 * the Concierge® already knows the neighbourhood. These 5 components
 * carry that signal across.
 *
 * All components accept an optional `T` token map; the default matches
 * Maison's palette (deeper brass #8B6914). Each pillar passes its own
 * T from its module-local tokens for visual cohesion.
 */
import React from "react";

// Default token map (kept identical to Maison so existing usage works
// without explicit `T` override).
const DEFAULT_T = {
  bg:    "#F4EFE6",
  card:  "#FCFAF4",
  ink:   "#211C15",
  ink2:  "#5C5447",
  ink3:  "#8A8071",
  line:  "#E5DCCB",
  line2: "#D8CDB7",
  brass: "#8B6914",
};

const SECTION_HEAD = (T) => ({
  fontSize: 11,
  color: T.ink3,
  letterSpacing: "0.16em",
  textTransform: "uppercase",
  fontWeight: 600,
  margin: "0 0 10px",
  fontFamily: "ui-monospace, monospace",
});

/**
 * Concierge® voice line · italic Fraunces blockquote with brass left
 * border, "— the Concierge®" footer. The atmosphere lift.
 */
export function DrawerVoiceLine({ text, T = DEFAULT_T, testid = "drawer-voice-line" }) {
  if (!text) return null;
  return (
    <blockquote
      data-testid={testid}
      style={{
        borderLeft: `2px solid ${T.brass}`,
        padding: "8px 0 8px 16px",
        margin: "0 0 16px",
        fontStyle: "italic",
        color: T.ink,
        fontSize: 15,
        lineHeight: 1.55,
        fontFamily: "'Fraunces', 'EB Garamond', serif",
      }}
    >
      {text}
      <footer style={{
        marginTop: 6,
        fontSize: 10.5,
        color: T.brass,
        fontFamily: "ui-monospace, monospace",
        fontStyle: "normal",
        letterSpacing: "0.18em",
        textTransform: "uppercase",
        fontWeight: 600,
      }}>
        — the Concierge®
      </footer>
    </blockquote>
  );
}

/**
 * Cross-pillar "Around the corner" whispers — hotel & restaurant
 * walking distances. Powered by /api/distance/eta upstream; this
 * component only renders.
 *
 * Accepts shape: { hotel?: { name, walk_min }, restaurant?: { name, walk_min } }
 * Optional `extras` — array of { label, name, walk_min } for future
 * pillars that may carry more than two neighbours.
 */
export function DrawerNeighbours({ neighbours, extras = [], T = DEFAULT_T, testid = "drawer-neighbours" }) {
  const hotel = neighbours?.hotel;
  const restaurant = neighbours?.restaurant;
  if (!hotel && !restaurant && (!extras || extras.length === 0)) return null;
  return (
    <section
      data-testid={testid}
      style={{
        borderTop: `1px solid ${T.line}`,
        padding: "14px 0 4px",
        marginBottom: 8,
      }}
    >
      <p style={SECTION_HEAD(T)}>Around the corner</p>
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {hotel && (
          <NeighbourRow walkMin={hotel.walk_min} name={hotel.name} T={T} />
        )}
        {restaurant && (
          <NeighbourRow walkMin={restaurant.walk_min} name={restaurant.name} T={T} />
        )}
        {extras.map((n, i) => (
          <NeighbourRow key={i} walkMin={n.walk_min} name={n.name} label={n.label} T={T} />
        ))}
      </div>
    </section>
  );
}

function NeighbourRow({ walkMin, name, label, T }) {
  if (!name) return null;
  return (
    <p style={{ fontSize: 13.5, color: T.ink, margin: 0, lineHeight: 1.5 }}>
      <span style={{ color: T.brass, marginRight: 6 }}>◆</span>
      {walkMin != null && (
        <strong style={{ fontWeight: 600 }}>{walkMin} min walk</strong>
      )}
      {walkMin != null ? " to " : null}
      {label ? <span style={{ color: T.ink2 }}>{label} · </span> : null}
      <em style={{ fontStyle: "normal", color: T.ink2 }}>{name}</em>
    </p>
  );
}

/**
 * Member whisper · italic brass line shown when ≥3 briefs landed on
 * the same venue.
 */
export function DrawerMemberWhisper({ text, T = DEFAULT_T, testid = "drawer-member-whisper" }) {
  if (!text) return null;
  return (
    <p
      data-testid={testid}
      style={{
        fontSize: 12,
        color: T.brass,
        fontStyle: "italic",
        borderTop: `1px solid ${T.line}`,
        paddingTop: 10,
        margin: "0 0 14px",
        lineHeight: 1.5,
      }}
    >
      {text}
    </p>
  );
}

/**
 * Reviews band · "Trusted voices" with italic review quotes,
 * author, rating, and time-ago. Pass the array directly from
 * Place Details.
 */
export function DrawerReviewsBand({ reviews, limit = 2, T = DEFAULT_T, testid = "drawer-reviews" }) {
  if (!reviews || reviews.length === 0) return null;
  return (
    <section
      data-testid={testid}
      style={{
        borderTop: `1px solid ${T.line}`,
        padding: "14px 0 4px",
        marginBottom: 8,
      }}
    >
      <p style={{ ...SECTION_HEAD(T), margin: "0 0 12px" }}>Trusted voices</p>
      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        {reviews.slice(0, limit).map((rv, i) => (
          <div key={i} data-testid={`${testid}-item`}>
            <p style={{
              fontSize: 13.5,
              color: T.ink,
              fontStyle: "italic",
              margin: "0 0 4px",
              lineHeight: 1.55,
            }}>
              “{rv.text && rv.text.length > 200 ? rv.text.slice(0, 200).trim() + "…" : (rv.text || "")}”
            </p>
            <p style={{
              fontSize: 11,
              color: T.ink3,
              margin: 0,
              fontFamily: "ui-monospace, monospace",
              letterSpacing: "0.04em",
            }}>
              — {rv.author}
              {rv.rating ? <> · <span style={{ color: T.brass }}>★ {rv.rating}</span></> : null}
              {rv.time_text ? <> · {rv.time_text}</> : null}
            </p>
          </div>
        ))}
      </div>
    </section>
  );
}

/**
 * Pinned CTA footer · sticks to the bottom of the drawer regardless
 * of content length. The brief button is the only primary action;
 * the italic line under it carries the editorial signature.
 *
 * Props:
 *   onBrief — click handler
 *   label   — button copy, e.g. "Send a brief about Park Hyatt"
 *   note    — italic line under the button (default keeps Maison's
 *             "One brief. One Concierge®. The Concierge® arranges
 *             the rest.")
 */
export function DrawerPinnedFooter({
  onBrief,
  label,
  note = "One brief. One Concierge®. The Concierge® arranges the rest.",
  T = DEFAULT_T,
  testid = "drawer-pinned-footer",
  buttonTestid = "drawer-brief-cta",
}) {
  return (
    <div
      data-testid={testid}
      style={{
        background: T.bg,
        borderTop: `1px solid ${T.line}`,
        padding: "14px 28px 18px",
        flexShrink: 0,
        boxShadow: "0 -6px 18px rgba(33,28,21,0.06)",
      }}
    >
      <button
        type="button"
        data-testid={buttonTestid}
        onClick={onBrief}
        style={{
          background: T.ink,
          color: T.bg,
          border: 0,
          borderRadius: 999,
          padding: "13px 22px",
          fontSize: 14,
          fontWeight: 600,
          cursor: "pointer",
          width: "100%",
          fontFamily: "inherit",
        }}
      >
        {label || "Send a brief"}
      </button>
      <p style={{
        fontSize: 11.5,
        color: T.ink3,
        textAlign: "center",
        margin: "8px 0 0",
        fontStyle: "italic",
      }}>
        {note}
      </p>
    </div>
  );
}
